import PropTypes from 'prop-types';
import React from 'react';
import {
  FieldContainer,
  Label,
  Required,
  StyledSelect,
  HelperText,
} from './StyledComponents';

/**
 * Select - A reusable form select/dropdown component
 *
 * @param {string} label - Label text for the select
 * @param {boolean} required - Whether the field is required (shows red asterisk)
 * @param {string|number} value - The selected value
 * @param {Function} onChange - Callback when value changes
 * @param {string} placeholder - Placeholder option text
 * @param {Array} options - Array of {label, value} objects for options
 * @param {string} helperText - Helper/error text below the select
 * @param {boolean} error - Whether the select has an error state
 * @param {string} width - Width of the select (default: "100%")
 * @param {string} maxWidth - Max width of the select
 */
const Select = ({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  options = [],
  helperText,
  error = false,
  width = '100%',
  maxWidth,
  ...props
}) => {
  return (
    <FieldContainer width={width} maxWidth={maxWidth}>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}
      <StyledSelect value={value} onChange={onChange} error={error} {...props}>
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option, index) => (
          <option key={option.value ?? index} value={option.value}>
            {option.label}
          </option>
        ))}
      </StyledSelect>
      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </FieldContainer>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
  helperText: PropTypes.string,
  error: PropTypes.bool,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
};

export default Select;
